// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { Option } from "@insite/client-framework/Types/FieldDefinition";
import get from "@insite/mobius/utilities/get";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import systemColors from "@vanmeter/XDL/PreStyle/colors";

/**
 * Convert hex value to RGB
 * @param hex
 * @param alpha
 */
export const hexToRgb = (hex: any, alpha: number): string => {
    if (typeof hex === "function") {
        hex = hex();
    }
    if (!hex || typeof hex !== "string") {
        return hex;
    }
    // the color is  already rgb
    if (hex.includes("rgb")) {
        return hex;
    }
    // convert hex to rgba
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const getSystemColor = (color = "", alpha = 1, asHex = false) => {
    const getColorAsString = () => {
        const colors = systemColors;
        const tempColor = get(colors, `${color}`);
        if (typeof tempColor === "string") {
            return tempColor;
        }

        // return color.main if it exists (e.g. "primary")
        const tempResult = get(tempColor, "main");
        if (typeof tempResult === "string") {
            return tempResult;
        }

        // couldn't find color anywhere in the theme, pass it through (e.g. CSS color value)
        return color;
    };
    if (asHex) {
        return getColorAsString();
    }
    return hexToRgb(getColorAsString(), alpha);
};

/**
 * Get all colors in the object
 * @param colors
 */
export const getAllColorsAsDropdownOptions = (colors: { [key: string]: any }): Option<string>[] => {
    let options: Option<string>[] = [];
    const getDeepKeys = (colors: { [key: string]: any }): string[] => {
        let keys: string[] = [];
        for (const key in colors) {
            if (typeof colors[key] !== "object") {
                keys.push(key);
            }
            if (typeof colors[key] === "object") {
                const subKeys = getDeepKeys(colors[key]);
                keys = keys.concat(
                    subKeys.map(subKey => {
                        return `${key}.${subKey}`;
                    }),
                );
            }
        }
        return keys;
    };

    options = getDeepKeys(colors).map(option => ({
        displayName: `${option}`,
        value: `${get(colors, option)}`,
    }));
    return options;
};

/**
 * Get all colors in the object
 */
export const getSystemColorsAsDropdownOptions = (): Option<string>[] => {
    return getAllColorsAsDropdownOptions(systemColors);
};

/**
 * Read color value from theme
 * @param path
 * @param alpha
 */
const color = (path = "", alpha = 1) => {
    const isSystemColor = get(systemColors, path);
    const isHexColor = /^#([0-9a-f]{3}){1,2}$/i.test(path);
    const isRGBColor =
        /^(rgb|hsl)(a?)[(]\s*([\d.]+\s*%?)\s*,\s*([\d.]+\s*%?)\s*,\s*([\d.]+\s*%?)\s*(?:,\s*([\d.]+)\s*)?[)]$/i.test(
            path,
        );

    return (props: any = {}) =>
        isSystemColor || isHexColor || isRGBColor ? hexToRgb(resolveColor(path, props.theme), alpha) : path;
};

export default color;
